// src/components/Pentests/tabs/tools/VulnScanLauncher.js
//
// Multi-engine vulnerability scan launcher. Operator picks one or more engines
// (currently nuclei + ZAP), each with its own profile (and for nuclei custom,
// its full category/tag config). Backend launches a single VulnScan with N
// VulnScanRunEngine children; results aggregate into a unified run history
// and findings flow into the Findings tab via the per-engine extractors.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Atom, Bug, Play } from 'lucide-react';
import { vulnScanAPI } from '../../../../services/api';
import { PrimaryCTA } from '../../../common/Shinobi';
import ProfileCombobox from './ProfileCombobox';
import CustomScanConfig from './CustomScanConfig';

const DEFAULT_NUCLEI_CUSTOM = {
  categories: ['http'],
  tags: [],
  rateLimit: 25,
  concurrency: 10,
  dastMode: false,
};

const ENGINE_META = {
  nuclei: {
    label: 'Nuclei',
    icon: Atom,
    accent: 'green',
    description: 'Template-based scanner. Fast, broad CVE + misconfig coverage.',
  },
  zap: {
    label: 'OWASP ZAP',
    icon: Bug,
    accent: 'orange',
    description: 'Active web app scan with spider + ascan. Slower but catches real injection vulns.',
  },
};

const ACCENT_CLASSES = {
  green:  { border: 'border-shinobi-green/60', bg: 'bg-shinobi-green/5',  text: 'text-shinobi-green', dot: 'bg-shinobi-green' },
  orange: { border: 'border-orange-400/60',    bg: 'bg-orange-500/5',     text: 'text-orange-700',    dot: 'bg-orange-400' },
};

export default function VulnScanLauncher({ pentest }) {
  const { id: pentestIdParam } = useParams();
  const pentestId = Number(pentestIdParam);
  const navigate = useNavigate();

  const [profiles, setProfiles] = useState([]);
  const [profileOptions, setProfileOptions] = useState(null);
  // Per-engine state. enabled controls whether this engine ships in the launch
  // payload. profile_name is required when enabled. nuclei custom carries its
  // own config blob; zap config is implicit in the profile (operator can pick
  // zap_passive / zap_active / zap_deep).
  const [engines, setEngines] = useState({
    nuclei: { enabled: true, profile_name: '', config: null },
    zap:    { enabled: false, profile_name: '', config: null },
  });
  const [nucleiCustom, setNucleiCustom] = useState(DEFAULT_NUCLEI_CUSTOM);
  const [targetsText, setTargetsText] = useState('');
  const [scanName, setScanName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([vulnScanAPI.getProfiles(), vulnScanAPI.getProfileOptions()])
      .then(([profilesResp, optionsResp]) => {
        if (cancelled) return;
        const list = Array.isArray(profilesResp?.data)
          ? profilesResp.data
          : (profilesResp?.data?.profiles || []);
        setProfiles(list);
        setProfileOptions(optionsResp?.data || null);
        // Default profile per engine = first non-custom profile of that engine
        setEngines((prev) => {
          const next = { ...prev };
          for (const engine of Object.keys(next)) {
            if (next[engine].profile_name) continue;
            const candidates = list.filter((p) => (p.engine || 'nuclei') === engine);
            const pick = candidates.find((p) => p.name !== 'custom') || candidates[0];
            if (pick) next[engine] = { ...next[engine], profile_name: pick.name || pick.id };
          }
          return next;
        });
      })
      .catch((e) => !cancelled && setError(e?.response?.data?.detail || e.message));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const profilesByEngine = useMemo(() => {
    const grouped = {};
    for (const p of profiles) {
      const eng = p.engine || 'nuclei';
      if (!grouped[eng]) grouped[eng] = [];
      grouped[eng].push(p);
    }
    return grouped;
  }, [profiles]);

  const scopeChips = useMemo(() => {
    const sc = pentest?.scope_targets || {};
    return [
      ...(sc.ips || []),
      ...(sc.urls || []),
      ...(sc.hostnames || []),
    ].filter(Boolean);
  }, [pentest]);

  const enabledEngines = useMemo(
    () => Object.entries(engines).filter(([, v]) => v.enabled).map(([k]) => k),
    [engines],
  );

  const totalDurationEstimate = useMemo(() => {
    let mins = 0;
    for (const eng of enabledEngines) {
      const prof = profiles.find((p) => p.name === engines[eng].profile_name && (p.engine || 'nuclei') === eng);
      if (prof?.estimated_duration_minutes) mins += prof.estimated_duration_minutes;
    }
    return mins;
  }, [enabledEngines, engines, profiles]);

  const validateBeforeSubmit = () => {
    if (enabledEngines.length === 0) return 'Pick at least one engine';
    for (const eng of enabledEngines) {
      const spec = engines[eng];
      if (!spec.profile_name) return `${ENGINE_META[eng]?.label || eng}: pick a profile`;
      if (eng === 'nuclei' && spec.profile_name === 'custom') {
        if (nucleiCustom.categories.length === 0) {
          return 'Nuclei custom config requires at least one category';
        }
      }
    }
    return null;
  };

  const onLaunch = async (e) => {
    e.preventDefault();
    const targetList = targetsText
      .split('\n')
      .map((t) => t.trim())
      .filter(Boolean);
    if (targetList.length === 0) {
      setError('Add at least one target');
      return;
    }
    const validationError = validateBeforeSubmit();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError(null);
    setInfo(null);
    try {
      // Multi-engine payload. The backend normalizes into engine_configs.
      const enginesPayload = enabledEngines.map((eng) => {
        const spec = engines[eng];
        const out = { engine: eng, profile_name: spec.profile_name };
        if (eng === 'nuclei' && spec.profile_name === 'custom') {
          out.config = {
            categories: nucleiCustom.categories,
            tags: nucleiCustom.tags,
            rate_limit: nucleiCustom.rateLimit,
            concurrency: nucleiCustom.concurrency,
            dast_mode: nucleiCustom.dastMode,
          };
        }
        return out;
      });

      const body = {
        engines: enginesPayload,
        targets: targetList,
        name: scanName.trim() || undefined,
        pentest_id: pentestId,
      };

      const created = await vulnScanAPI.create(body);
      const vulnScanId = created?.data?.id;
      if (!vulnScanId) throw new Error('No id in vuln-scan create response');

      const runResp = await vulnScanAPI.run(vulnScanId, { pentest_id: pentestId });
      const runId = runResp?.data?.run_id;
      const engineLabels = enabledEngines.map((e) => ENGINE_META[e]?.label || e).join(' + ');
      setInfo(`${engineLabels} scan #${vulnScanId} launched (run #${runId}). View in the Runs tab.`);
      setTargetsText('');
      setScanName('');
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const appendChip = (val) => {
    setTargetsText((prev) => (prev ? `${prev}\n${val}` : val));
  };

  const setEngineEnabled = (engine, enabled) => {
    setEngines((prev) => ({ ...prev, [engine]: { ...prev[engine], enabled } }));
  };
  const setEngineProfile = (engine, profile_name) => {
    setEngines((prev) => ({ ...prev, [engine]: { ...prev[engine], profile_name } }));
  };

  return (
    <form onSubmit={onLaunch} className="space-y-5 max-w-2xl">
      <div>
        <label className="block text-sm font-medium text-slate-800 mb-2">Engines</label>
        <p className="text-xs text-slate-500 mb-3">
          Pick one or more engines to run in parallel against the same targets. Each engine's
          findings flow into the Findings tab; the run history shows one row with per-engine
          status pills.
        </p>
        <div className="space-y-3">
          {Object.entries(ENGINE_META).map(([engine, meta]) => {
            const Icon = meta.icon;
            const accent = ACCENT_CLASSES[meta.accent] || ACCENT_CLASSES.green;
            const spec = engines[engine];
            const engineProfiles = profilesByEngine[engine] || [];
            const isCustomNuclei = engine === 'nuclei' && spec.profile_name === 'custom';
            return (
              <div
                key={engine}
                className={`rounded-lg border p-3 transition ${
                  spec.enabled
                    ? `${accent.border} ${accent.bg}`
                    : 'border-cyber-300/40 bg-cyber-100/30 hover:border-cyber-300/60'
                }`}
              >
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={spec.enabled}
                    onChange={(e) => setEngineEnabled(engine, e.target.checked)}
                    className="mt-1 w-4 h-4 accent-shinobi-green"
                  />
                  <div className={`flex items-center justify-center w-9 h-9 rounded-lg border ${accent.border} ${accent.bg} ${accent.text} shrink-0`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">{meta.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{meta.description}</div>
                  </div>
                </label>

                {spec.enabled && (
                  <div className="ml-7 mt-3 space-y-3">
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">
                        Profile
                      </label>
                      <ProfileCombobox
                        profiles={engineProfiles}
                        value={spec.profile_name}
                        onChange={(v) => setEngineProfile(engine, v)}
                      />
                    </div>
                    {isCustomNuclei && (
                      <CustomScanConfig
                        options={profileOptions}
                        categories={nucleiCustom.categories}
                        tags={nucleiCustom.tags}
                        rateLimit={nucleiCustom.rateLimit}
                        concurrency={nucleiCustom.concurrency}
                        dastMode={nucleiCustom.dastMode}
                        onChangeCategories={(v) => setNucleiCustom((c) => ({ ...c, categories: v }))}
                        onChangeTags={(v) => setNucleiCustom((c) => ({ ...c, tags: v }))}
                        onChangeRateLimit={(v) => setNucleiCustom((c) => ({ ...c, rateLimit: v }))}
                        onChangeConcurrency={(v) => setNucleiCustom((c) => ({ ...c, concurrency: v }))}
                        onChangeDastMode={(v) => setNucleiCustom((c) => ({ ...c, dastMode: v }))}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-800 mb-2">Targets (one per line)</label>
        <textarea
          value={targetsText}
          onChange={(e) => setTargetsText(e.target.value)}
          rows={5}
          placeholder={'example.com\n198.51.100.10\nhttps://app.example.com'}
          className="w-full bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 text-slate-900 focus:border-shinobi-green outline-none font-mono text-sm"
          required
        />
        {scopeChips.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span className="text-xs text-slate-500 mr-1 self-center">Add from scope:</span>
            {scopeChips.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => appendChip(t)}
                className="px-2 py-0.5 text-xs rounded border border-cyber-300/60 bg-cyber-100 text-slate-700 hover:border-shinobi-green hover:text-shinobi-green"
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-800 mb-2">Scan Name (optional)</label>
        <input
          type="text"
          value={scanName}
          onChange={(e) => setScanName(e.target.value)}
          placeholder="Default: auto-generated from engines + timestamp"
          className="w-full bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 text-slate-900 focus:border-shinobi-green outline-none"
        />
      </div>

      {error && <div className="text-red-600 text-sm">{error}</div>}
      {info && (
        <div className="text-shinobi-green text-sm">
          {info}{' '}
          <button
            type="button"
            onClick={() => navigate(`/pentests/${pentestId}/runs`)}
            className="underline"
          >
            Open Runs
          </button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {enabledEngines.length === 0
            ? 'Pick at least one engine to launch.'
            : `Launching ${enabledEngines.length} engine${enabledEngines.length === 1 ? '' : 's'}${totalDurationEstimate ? ` - estimated ~${totalDurationEstimate}min per target` : ''}.`}
        </div>
        <PrimaryCTA
          variant="green"
          type="submit"
          loading={submitting}
          icon={Play}
          iconPosition="left"
          disabled={profiles.length === 0 || enabledEngines.length === 0}
        >
          Launch Vulnerability Scan
        </PrimaryCTA>
      </div>
    </form>
  );
}
